/**
 * api.js
 *
 * Central API abstraction for all frontend-backend communication in HomeIQ.
 *
 * Responsibilities:
 * - Handles HTTP requests for authentication, property, transfer, log, and admin actions
 * - Manages auth tokens and error handling
 * - Exports functions for all major backend endpoints
 *
 * Used by: React context, pages, and components throughout the app
 */

const API_URL = import.meta.env.VITE_API_URL || '/api'

function getToken() {
  return localStorage.getItem('heirloom_token')
}

async function request(path, options = {}) {
  const token = getToken()
  const headers = { 'Content-Type': 'application/json', ...options.headers }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_URL}${path}`, { ...options, headers })
  let data
  const contentType = res.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    try {
      data = await res.json()
    } catch {
      data = {}
    }
  } else {
    data = {}
  }

  if (!res.ok) {
    throw new Error(data.error || `Request failed with status ${res.status}`)
  }
  return data
}

// --- Transfer Requests ---
// Get approved transfer history for a property (owner only)
export async function getPropertyTransferHistory(propertyId) {
  const data = await request(`/ownership/transfer/history/${encodeURIComponent(propertyId)}`)
  return data.transfers
}

// Get transfer requests for current user (as owner)
export async function getTransferRequests() {
  const data = await request('/ownership/transfer/requests')
  return data.requests
}

// Get transfer requests for current user (as requester)
export async function getMyTransferRequests() {
  const data = await request('/ownership/transfer/my-requests')
  return data.requests
}

// Approve or reject a transfer request
export async function handleTransferRequest(propertyId, requestId, action) {
  const data = await request('/ownership/transfer/handle', {
    method: 'POST',
    body: JSON.stringify({ propertyId, requestId, action }),
  })
  return data
}

// --- Properties ---
export async function getProperties() {
  const data = await request('/ownership/properties')
  return data.properties
}

export async function addProperty({ address, city, state, zip, location }) {
  const data = await request('/ownership/properties', {
    method: 'POST',
    body: JSON.stringify({ address, city, state, zip, ...(location && { location }) }),
  })
  return data.property
}

export async function removeProperty(propertyId) {
  await request(`/ownership/properties/${encodeURIComponent(propertyId)}`, { method: 'DELETE' })
}

// Auth
export async function register(form) {
  // Clear any stale impersonation state before creating a new session
  localStorage.removeItem('heirloom_admin_token')
  const data = await request('/auth/register', {
    method: 'POST',
    body: JSON.stringify(form),
  })
  localStorage.setItem('heirloom_token', data.token)
  return data.user
}

export async function login(email, password) {
  // Clear any stale impersonation state before creating a new session
  localStorage.removeItem('heirloom_admin_token')
  const data = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  localStorage.setItem('heirloom_token', data.token)
  return data.user
}

// Exchange a single-use OAuth code (received via ?code= in the redirect URL)
// for a session JWT. The JWT comes back in the response body — never in the URL.
// See server/routes/auth.js:oauthSuccess + /oauth-exchange for the matching server flow.
export async function oauthExchange(code) {
  const data = await request('/auth/oauth-exchange', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
  localStorage.setItem('heirloom_token', data.token)
  return data.user
}

export async function loginWithGoogle(credential) {
  // Clear any stale impersonation state before creating a new session
  localStorage.removeItem('heirloom_admin_token')
  const data = await request('/auth/google', {
    method: 'POST',
    body: JSON.stringify({ credential }),
  })
  localStorage.setItem('heirloom_token', data.token)
  return data.user
}

export function logout() {
  localStorage.removeItem('heirloom_token')
  localStorage.removeItem('heirloom_admin_token')
}

export async function getMe() {
  const data = await request('/auth/me')
  return data.user
}

export async function getAgentProfile(agentId) {
  const data = await request(`/auth/profile/${agentId}`)
  return data.agent
}

export async function verifyEmail(code) {
  const data = await request('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
  return data.user
}

export async function resendVerification() {
  await request('/auth/resend-verification', { method: 'POST' })
}

export async function forgotPassword(email) {
  const data = await request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
  return data
}

export async function resetPassword(token, password) {
  const data = await request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  })
  return data
}

export async function setPassword(password) {
  const data = await request('/auth/set-password', {
    method: 'POST',
    body: JSON.stringify({ password }),
  })
  return data
}

export async function changePassword(currentPassword, newPassword) {
  const data = await request('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  })
  return data
}

export async function updateProfile(updates) {
  const data = await request('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
  return data.user
}

// Log Entries
// Options: { page, limit, propertyId, contractorId }
// Returns { entries, total, hasMore } — or just entries array when called without options (backward compat).
export async function getLogEntries({ page, limit, propertyId, contractorId } = {}) {
  const params = new URLSearchParams()
  if (page) params.set('page', page)
  if (limit) params.set('limit', limit)
  if (propertyId) params.set('propertyId', propertyId)
  if (contractorId) params.set('contractorId', contractorId)
  const qs = params.toString()
  const data = await request(`/logs${qs ? `?${qs}` : ''}`)
  // Paginated call: return full response object
  if (page) return data
  // Unpaginated: backward-compat — return just the entries array
  return data.entries
}

export async function createLogEntry(entry) {
  const data = await request('/logs', {
    method: 'POST',
    body: JSON.stringify(entry),
  })
  return data.entry
}

export async function updateLogEntry(id, updates) {
  const data = await request(`/logs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
  return data.entry
}

export async function deleteLogEntry(id) {
  await request(`/logs/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function updateLogEntryNote(id, ownerNote) {
  const data = await request(`/logs/${encodeURIComponent(id)}/note`, {
    method: 'PATCH',
    body: JSON.stringify({ ownerNote }),
  })
  return data.entry
}


// Property data
export async function getPropertyData(address) {
  const data = await request(`/property?address=${encodeURIComponent(address)}`)
  return data.property
}

// Disclosures
export async function getDisclosures(propertyId) {
  const qs = propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''
  const data = await request(`/disclosures${qs}`)
  return data.disclosures
}

export async function createDisclosure({ type, text, status }) {
  const data = await request('/disclosures', {
    method: 'POST',
    body: JSON.stringify({ type, text, status }),
  })
  return data.disclosure
}

export async function addDisclosureNote(id, text) {
  const data = await request(`/disclosures/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
  return data.disclosure
}

export async function getPublicDisclosures(userId, propertyId) {
  const data = await request(`/disclosures/public/${encodeURIComponent(userId)}/${encodeURIComponent(propertyId)}`)
  return data.disclosures
}

// Parse a fetch response body in a way that surfaces real upstream errors.
// Proxies (Render, Cloudflare) often return HTML for 5xx — without this helper,
// callers see "status 502" with no detail and Sentry has no debug trail.
async function parseUploadResponse(res, fallbackMsg) {
  const contentType = res.headers.get('content-type') || ''
  let data = {}
  if (contentType.includes('application/json')) {
    try { data = await res.json() } catch { /* malformed JSON */ }
  } else if (!res.ok) {
    try {
      const body = await res.text()
      data = { error: body.slice(0, 500).trim() || `${fallbackMsg} (status ${res.status})` }
    } catch { /* body unreadable */ }
  }
  if (!res.ok) throw new Error(data.error || `${fallbackMsg} (status ${res.status})`)
  return data
}

// Document extraction — uploads a File object (PDF/DOCX/TXT) to the server for text extraction
export async function extractDocumentText(file) {
  const token = getToken()
  const form = new FormData()
  form.append('file', file)
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${API_URL}/extract`, { method: 'POST', body: form, headers })
  const data = await parseUploadResponse(res, 'Upload failed')
  // Returns { text, fileId, fileName, extractFailed?, extractError? }
  // extractFailed=true means file was stored but text could not be read (scanned/encrypted PDF)
  return { text: data.text, fileId: data.fileId, fileName: data.fileName, extractFailed: data.extractFailed, extractError: data.extractError }
}

// Disclosures — parse
export async function parseDisclosure(text) {
  const data = await request('/disclosures/parse', {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
  return data.items
}

// Disclosures — extract form fields from raw document text
export async function extractDisclosureFields(text) {
  const data = await request('/disclosures/extract-fields', {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
  return data
}

// Disclosures — generate AI report from property form data
export async function generateDisclosure(formData) {
  const data = await request('/disclosures/generate', {
    method: 'POST',
    body: JSON.stringify(formData),
  })
  return data
}

// Disclosures — batch save (from import)
export async function batchCreateDisclosures(items) {
  const data = await request('/disclosures/batch', {
    method: 'POST',
    body: JSON.stringify({ items }),
  })
  return data.disclosures
}

// Disclosures — toggle exclude from public report
export async function toggleDisclosureExclude(id) {
  const data = await request(`/disclosures/${encodeURIComponent(id)}/exclude`, { method: 'PATCH' })
  return data.disclosure
}

// Generated disclosure reports (realtor plan)
export async function getGeneratedDisclosures() {
  const data = await request('/disclosures/generated')
  return data.reports
}

export async function getGeneratedDisclosure(id) {
  const data = await request(`/disclosures/generated/${encodeURIComponent(id)}`)
  return data.report
}

export async function deleteGeneratedDisclosure(id) {
  await request(`/disclosures/generated/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// Realtor disclosure template upload — returns { fileId, fileName, text }
export async function uploadDisclosureTemplate(file) {
  const token = getToken()
  const body = new FormData()
  body.append('file', file)
  const res = await fetch(`${API_URL}/disclosures/template`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || 'Template upload failed')
  }
  return res.json()
}

export async function getDisclosureTemplate() {
  const data = await request('/disclosures/template')
  return data.template // null if none set, or { fileId, fileName, text }
}

export async function deleteDisclosureTemplate() {
  await request('/disclosures/template', { method: 'DELETE' })
}

// Create a Stripe checkout session for purchasing a disclosure credit (homeowners)
export async function createDisclosureCheckout() {
  const data = await request('/stripe/checkout', {
    method: 'POST',
    body: JSON.stringify({ productKey: 'disclosure_report' }),
  })
  return data // { url }
}

// Inspections
export async function parseInspection(text) {
  const data = await request('/inspections/parse', {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
  return data
}

export async function parseInspectionFile(file, propertyId) {
  const token = getToken()
  const form = new FormData()
  form.append('file', file)
  if (propertyId) form.append('propertyId', propertyId)
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${API_URL}/inspections/parse-file`, { method: 'POST', body: form, headers })
  return parseUploadResponse(res, 'Inspection parse failed')
}

export async function uploadInspectionFile(file, propertyId) {
  const token = getToken()
  const form = new FormData()
  form.append('file', file)
  if (propertyId) form.append('propertyId', propertyId)
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${API_URL}/inspections/upload`, { method: 'POST', body: form, headers })
  const data = await parseUploadResponse(res, 'Upload failed')
  return data.inspection
}

export async function getInspections(propertyId) {
  const qs = propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''
  const data = await request(`/inspections${qs}`)
  return data.inspections
}

export async function createInspection({ propertyId, label, inspectorName, inspectedAt, summary, issues, fileId, fileName }) {
  const data = await request('/inspections', {
    method: 'POST',
    body: JSON.stringify({ propertyId, label, inspectorName, inspectedAt, summary, issues, fileId, fileName }),
  })
  return data.inspection
}

export async function addInspectionIssueNote(inspectionId, issueIndex, text) {
  const data = await request(`/inspections/${encodeURIComponent(inspectionId)}/issues/${issueIndex}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
  return data.inspection
}

export async function toggleInspectionExclude(id) {
  const data = await request(`/inspections/${encodeURIComponent(id)}/exclude`, { method: 'PATCH' })
  return data.inspection
}

export async function deleteInspection(id) {
  await request(`/inspections/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function resolveInspectionIssue(inspectionId, issueIndex, note = '') {
  const data = await request(`/inspections/${encodeURIComponent(inspectionId)}/issues/${issueIndex}/resolve`, {
    method: 'PATCH',
    body: JSON.stringify({ note }),
  })
  return data.inspection
}

export async function reopenInspectionIssue(inspectionId, issueIndex) {
  const data = await request(`/inspections/${encodeURIComponent(inspectionId)}/issues/${issueIndex}/reopen`, {
    method: 'PATCH',
  })
  return data.inspection
}

export async function toggleLogEntryExclude(id) {
  const data = await request(`/logs/${encodeURIComponent(id)}/exclude`, { method: 'PATCH' })
  return data.entry
}

// Property Reminders
export async function getReminders(propertyId) {
  const data = await request(`/reminders?propertyId=${encodeURIComponent(propertyId)}`)
  return data.reminders
}

export async function createReminder({ propertyId, label, system, intervalDays, presetSystem, disabled }) {
  const data = await request('/reminders', {
    method: 'POST',
    body: JSON.stringify({ propertyId, label, system, intervalDays, presetSystem, disabled }),
  })
  return data.reminder
}

export async function updateReminder(id, { label, system, intervalDays, disabled }) {
  const data = await request(`/reminders/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ label, system, intervalDays, disabled }),
  })
  return data.reminder
}

export async function deleteReminder(id) {
  await request(`/reminders/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function getPublicInspections(userId, propertyId) {
  const data = await request(`/inspections/public/${encodeURIComponent(userId)}/${encodeURIComponent(propertyId)}`)
  return data.inspections
}

// Reports
export async function getReports() {
  const data = await request('/reports')
  return data.reports
}

export async function createReport(report) {
  const data = await request('/reports', {
    method: 'POST',
    body: JSON.stringify(report),
  })
  return data.report
}

export async function purchaseListingAccess(listingUserId, address, listingPropertyId) {
  return createReport({ type: 'buyer', address, listingUserId, listingPropertyId })
}

export async function deleteReport(id) {
  await request(`/reports/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// Ownership
export async function verifyOwnership(method, image, propertyId) {
  const data = await request('/ownership/verify', {
    method: 'POST',
    body: JSON.stringify({ method, image, propertyId }),
  })
  return data
}

export async function toggleListing(active, price, propertyId, sellingIntent) {
  const data = await request('/ownership/listing', {
    method: 'POST',
    body: JSON.stringify({ active, price, propertyId, sellingIntent }),
  })
  return data.property
}


export async function searchListings(q) {
  // Public endpoint — no auth token needed
  try {
    const res = await fetch(`${API_URL}/logs/search?q=${encodeURIComponent(q)}`)
    if (!res.ok) return { listings: [] }
    return await res.json()
  } catch {
    return { listings: [] }
  }
}

export async function checkListingByAddress(address) {
  // Public endpoint — no auth token needed
  try {
    const res = await fetch(`${API_URL}/logs/listing-check?address=${encodeURIComponent(address)}`)
    if (!res.ok) return { hasListing: false }
    return await res.json()
  } catch {
    return { hasListing: false }
  }
}

export async function getPublicLogs(userId, propertyId) {
  const data = await request(`/logs/public/${encodeURIComponent(userId)}/${encodeURIComponent(propertyId)}`)
  return data
}

export function isLoggedIn() {
  return !!getToken()
}

// ─── Stripe ───────────────────────────────────────────────────────────────

// productKey: 'single_report' | 'buyer_pro' | 'realtor' | 'realtor_pro'
// Returns a Stripe Checkout URL — redirect the browser to it.
export async function createCheckoutSession(productKey) {
  const data = await request('/stripe/checkout', {
    method: 'POST',
    body: JSON.stringify({ productKey }),
  })
  return data.url
}

// Returns a Stripe Billing Portal URL for managing subscriptions.
export async function getBillingPortalUrl() {
  const data = await request('/stripe/portal')
  return data.url
}

// Returns live subscription details: { subscription: { amount, currency, interval, currentPeriodEnd, status } }
export async function getSubscription() {
  return request('/stripe/subscription')
}

// ─── Admin ────────────────────────────────────────────────────────────────

export async function adminListUsers({ search = '', page = 1, limit = 50 } = {}) {
  const params = new URLSearchParams({ search, page, limit })
  const data = await request(`/admin/users?${params}`)
  return data
}

export async function adminUpdateUser(id, updates) {
  const data = await request(`/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
  return data.user
}

export async function adminDeleteUser(id) {
  await request(`/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function adminGetUserProperties(id) {
  const data = await request(`/admin/users/${encodeURIComponent(id)}/properties`)
  return data.properties || []
}

export async function adminImpersonate(id) {
  // Defense against impersonation chains: if heirloom_admin_token is already set,
  // we're already impersonating someone. Overwriting it with the current (already
  // impersonated) token would destroy the original admin's restore token and the
  // admin would lose their session permanently. Server-side blockDuringImpersonation
  // also rejects the request on /admin/impersonate when the caller is impersonated,
  // but the client check gives a clearer error before round-tripping.
  if (localStorage.getItem('heirloom_admin_token')) {
    throw new Error('Already impersonating a user. Exit impersonation before impersonating someone else.')
  }
  const data = await request(`/admin/impersonate/${encodeURIComponent(id)}`, { method: 'POST' })
  // Stash the real admin token, then swap in the impersonated token
  localStorage.setItem('heirloom_admin_token', getToken())
  localStorage.setItem('heirloom_token', data.token)
  return data.user
}

export function adminExitImpersonation() {
  const adminToken = localStorage.getItem('heirloom_admin_token')
  if (adminToken) {
    localStorage.setItem('heirloom_token', adminToken)
    localStorage.removeItem('heirloom_admin_token')
  }
}

export function isImpersonating() {
  return !!localStorage.getItem('heirloom_admin_token')
}

// Admin: Get all purchased reports for a user
export async function adminGetUserReports(userId) {
  const data = await request(`/admin-reports/user/${encodeURIComponent(userId)}/reports`)
  return data.reports
}

// Admin: Delete a report by ID
export async function adminDeleteReport(reportId) {
  await request(`/admin-reports/report/${encodeURIComponent(reportId)}`, { method: 'DELETE' })
}

// Admin: Create a new report for a user
export async function adminCreateReport(userId, report) {
  const data = await request(`/admin-reports/user/${encodeURIComponent(userId)}/reports`, {
    method: 'POST',
    body: JSON.stringify(report),
  })
  return data.report
}

// Admin: Seed / reset test accounts for all four plan types
export async function adminSeedTestAccounts() {
  const data = await request('/admin/seed-test-accounts', { method: 'POST' })
  return data
}

// Admin: Reset a user's credits to their plan defaults
export async function adminResetCredits(userId) {
  const data = await request(`/admin/users/${encodeURIComponent(userId)}/reset-credits`, { method: 'POST' })
  return data
}

// Admin: List all transfer requests across all users
export async function adminGetTransferRequests() {
  const data = await request('/ownership/admin/transfer-requests')
  return data.requests
}

// Admin: Approve, reject, or force-transfer a property
export async function adminTransferAction({ ownerId, propertyId, requestId, action, newOwnerId }) {
  const data = await request('/ownership/admin/transfer-action', {
    method: 'POST',
    body: JSON.stringify({ ownerId, propertyId, requestId, action, newOwnerId }),
  })
  return data
}

// Admin: Fetch verification document image as an object URL (blob)
export async function getTransferDocUrl(requestId) {
  const token = getToken()
  const res = await fetch(`${API_URL}/admin/transfer-doc/${encodeURIComponent(requestId)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) throw new Error('Document not available')
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}

// ─── Appliances ───────────────────────────────────────────────────────────────

export async function getAppliances(propertyId) {
  const data = await request(`/appliances?propertyId=${encodeURIComponent(propertyId)}`)
  return data.appliances || []
}

export async function createAppliance(payload) {
  const data = await request('/appliances', { method: 'POST', body: JSON.stringify(payload) })
  return data.appliance
}

export async function updateAppliance(id, payload) {
  const data = await request(`/appliances/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
  return data.appliance
}

export async function deleteAppliance(id) {
  return request(`/appliances/${id}`, { method: 'DELETE' })
}

// ─── Contractors ─────────────────────────────────────────────────────────────

export async function getContractors(propertyId) {
  const data = await request(`/contractors?propertyId=${encodeURIComponent(propertyId)}`)
  return data.contractors || []
}

export async function createContractor(payload) {
  const data = await request('/contractors', { method: 'POST', body: JSON.stringify(payload) })
  return data.contractor
}

export async function updateContractor(id, payload) {
  const data = await request(`/contractors/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
  return data.contractor
}

export async function deleteContractor(id) {
  return request(`/contractors/${id}`, { method: 'DELETE' })
}

// ─── Projects & Quotes ────────────────────────────────────────────────────────

export async function getProjects(propertyId) {
  const data = await request(`/projects?propertyId=${encodeURIComponent(propertyId)}`)
  return data.projects || []
}

export async function createProject(payload) {
  const data = await request('/projects', { method: 'POST', body: JSON.stringify(payload) })
  return data.project
}

export async function updateProject(id, payload) {
  const data = await request(`/projects/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(payload) })
  return data.project
}

export async function deleteProject(id) {
  return request(`/projects/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

function quoteFormData(fields, file) {
  const form = new FormData()
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null && value !== '') form.append(key, value)
  }
  if (file) form.append('file', file)
  return form
}

async function sendQuoteForm(path, method, fields, file) {
  const token = getToken()
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: quoteFormData(fields, file),
  })
  const data = await parseUploadResponse(res, 'Failed to save quote')
  return data.project
}

export async function addQuote(projectId, fields, file) {
  return sendQuoteForm(`/projects/${encodeURIComponent(projectId)}/quotes`, 'POST', fields, file)
}

export async function updateQuote(projectId, quoteId, fields, file) {
  return sendQuoteForm(
    `/projects/${encodeURIComponent(projectId)}/quotes/${encodeURIComponent(quoteId)}`,
    'PATCH',
    fields,
    file,
  )
}

export async function deleteQuote(projectId, quoteId) {
  const data = await request(
    `/projects/${encodeURIComponent(projectId)}/quotes/${encodeURIComponent(quoteId)}`,
    { method: 'DELETE' },
  )
  return data.project
}

export async function chooseQuote(projectId, quoteId) {
  const data = await request(`/projects/${encodeURIComponent(projectId)}/choose`, {
    method: 'POST',
    body: JSON.stringify({ quoteId }),
  })
  return data.project
}

export async function openQuoteFile(projectId, quoteId, fileName = 'quote') {
  const blob = await fetchProtectedBlob(
    `/projects/${encodeURIComponent(projectId)}/quotes/${encodeURIComponent(quoteId)}/file`,
    'Failed to load quote document',
  )
  const objectUrl = URL.createObjectURL(blob)
  const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer')

  if (!popup) {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    }, 0)
    return
  }

  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

// ─── Home Documents (Document Vault) ─────────────────────────────────────────

export async function getHomeDocuments(propertyId) {
  const data = await request(`/home-documents?propertyId=${encodeURIComponent(propertyId)}`)
  return data.documents || []
}

export async function uploadHomeDocument(propertyId, label, category, file) {
  const token = getToken()
  const form = new FormData()
  form.append('file', file)
  form.append('propertyId', propertyId)
  form.append('label', label)
  form.append('category', category)
  const res = await fetch(`${API_URL}/home-documents/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  const data = await parseUploadResponse(res, 'Upload failed')
  return data.document
}

export async function updateHomeDocument(id, payload) {
  const data = await request(`/home-documents/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
  return data.document
}

export async function deleteHomeDocument(id) {
  return request(`/home-documents/${id}`, { method: 'DELETE' })
}

async function fetchProtectedBlob(path, fallbackMsg) {
  const token = getToken()
  const headers = {}
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(`${API_URL}${path}`, { headers })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || `${fallbackMsg} (status ${res.status})`)
  }

  return res.blob()
}

export async function openHomeDocument(fileId, fileName = 'document') {
  const blob = await fetchProtectedBlob(`/home-documents/file/${encodeURIComponent(fileId)}`, 'Failed to load document')
  const objectUrl = URL.createObjectURL(blob)
  const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer')

  if (!popup) {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    }, 0)
    return
  }

  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

export async function fetchInspectionDocumentBlob(fileId) {
  return fetchProtectedBlob(`/inspections/file/${encodeURIComponent(fileId)}`, 'Failed to load inspection document')
}

export async function downloadInspectionDocument(fileId, fileName = 'inspection') {
  const blob = await fetchInspectionDocumentBlob(fileId)
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(objectUrl)
  }, 0)
}

// ─── Seller Disclosure Requests ───────────────────────────────────────────────

export async function createDisclosureRequest(payload) {
  const data = await request('/disclosure-requests', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return data
}

export async function getDisclosureRequests() {
  const data = await request('/disclosure-requests')
  return data.requests || []
}

export async function getDisclosureRequest(id) {
  const data = await request(`/disclosure-requests/${encodeURIComponent(id)}`)
  return data.request
}

export async function deleteDisclosureRequest(id) {
  await request(`/disclosure-requests/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function downloadDisclosureDocument(id) {
  const token = getToken()
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${API_URL}/disclosure-requests/${encodeURIComponent(id)}/download`, { headers })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Download failed')
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'seller_disclosure.docx'
  // Firefox requires the anchor to be in the document for click() to dispatch.
  // Safari needs the blob URL to outlive the synchronous tick so the download
  // can actually start — defer revoke to the next tick.
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 0)
}

// Public — no auth required
export async function getSellerFill(token) {
  const res = await fetch(`${API_URL}/disclosure-requests/fill/${encodeURIComponent(token)}`)
  return parseUploadResponse(res, 'Failed to load disclosure form')
}

export async function submitSellerFill(token, { sellerAnswers }) {
  const tokenValue = getToken()
  const headers = { 'Content-Type': 'application/json' }
  if (tokenValue) headers.Authorization = `Bearer ${tokenValue}`
  const res = await fetch(`${API_URL}/disclosure-requests/fill/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ sellerAnswers }),
  })
  return parseUploadResponse(res, 'Submission failed')
}

export async function updateSellerFillDraft(token, report) {
  return request(`/disclosure-requests/fill/${encodeURIComponent(token)}`, { method: 'PATCH', body: JSON.stringify({ report }) })
}

export async function approveSellerFillDraft(token) {
  return request(`/disclosure-requests/fill/${encodeURIComponent(token)}/approve`, { method: 'POST' })
}

// --- Property Sharing ---
export async function getShares(propertyId) {
  return request(`/property/${encodeURIComponent(propertyId)}/shares`)
}

export async function addShare(propertyId, email, access = 'read') {
  return request(`/property/${encodeURIComponent(propertyId)}/shares`, {
    method: 'POST',
    body: JSON.stringify({ email, access }),
  })
}

export async function removeShare(propertyId, email) {
  return request(`/property/${encodeURIComponent(propertyId)}/shares/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  })
}

// --- PWA Push Notifications ---
export async function getVapidPublicKey() {
  const res = await fetch(`${API_URL}/push/vapid-key`)
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Push not configured')
  return data.publicKey
}

export async function subscribeToPush(subscription) {
  return request('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify(subscription.toJSON()),
  })
}

export async function unsubscribeFromPush(endpoint) {
  return request('/push/unsubscribe', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  })
}

// Maintenance Alerts
export async function getAlerts(propertyId) {
  const data = await request(`/alerts?propertyId=${encodeURIComponent(propertyId)}`)
  return data.alerts || []
}

export async function snoozeAlert(id, days) {
  const data = await request(`/alerts/${encodeURIComponent(id)}/snooze`, {
    method: 'PATCH',
    body: JSON.stringify({ days }),
  })
  return data.alert
}

export async function dismissAlert(id) {
  const data = await request(`/alerts/${encodeURIComponent(id)}/dismiss`, { method: 'PATCH' })
  return data.alert
}

// Seasonal Alerts
export async function getSeasonalAlerts(propertyId) {
  const data = await request(`/seasonal-alerts?propertyId=${encodeURIComponent(propertyId)}`)
  return data.alerts || []
}

export async function createSeasonalAlert(payload) {
  const data = await request('/seasonal-alerts', { method: 'POST', body: JSON.stringify(payload) })
  return data.alert
}

export async function updateSeasonalAlert(id, patch) {
  const data = await request(`/seasonal-alerts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
  return data.alert
}

export async function deleteSeasonalAlert(id) {
  return request(`/seasonal-alerts/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// --- Feedback ---
export async function submitFeedback(payload) {
  // Works logged-in or anonymous — request() attaches the JWT only when present.
  return request('/feedback', { method: 'POST', body: JSON.stringify(payload) })
}

export async function adminListFeedback({ status = '', kind = '', category = '', page = 1, limit = 50 } = {}) {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (kind) params.set('kind', kind)
  if (category) params.set('category', category)
  params.set('page', page)
  params.set('limit', limit)
  return request(`/feedback?${params}`)
}

export async function adminUpdateFeedback(id, patch) {
  const data = await request(`/feedback/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
  return data.feedback
}

// --- Campaign leads ---

// Public — works without auth; request() omits the JWT header when absent.
export async function submitLead(payload) {
  return request('/leads', { method: 'POST', body: JSON.stringify(payload) })
}

// Admin — list captured leads (optionally filtered by status/audience).
export async function adminListLeads(params = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== '' && v != null)
  ).toString()
  return request(`/leads${qs ? `?${qs}` : ''}`)
}

// Admin — update a lead's status / note.
export async function adminUpdateLead(id, payload) {
  return request(`/leads/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
}

// Public — anonymous campaign pageview (best-effort analytics, fire-and-forget).
export async function recordCampaignView(payload) {
  return request('/campaign-events/view', { method: 'POST', body: JSON.stringify(payload) })
}

// Admin — campaign conversion funnel (views + leads by source).
export async function adminGetCampaignFunnel() {
  return request('/campaign-events/funnel')
}

export async function updateGeneratedDisclosure(id, report) {
  return request(`/disclosures/generated/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ report }) })
}
export async function approveGeneratedDisclosure(id) {
  return request(`/disclosures/generated/${encodeURIComponent(id)}/approve`, { method: 'POST' })
}

// --- Contractor accounts ---
export async function getContractorProfile() {
  return request('/contractor-profile/me')
}

export async function saveContractorProfile(profile) {
  return request('/contractor-profile/me', { method: 'PUT', body: JSON.stringify(profile) })
}

export async function getContractorJobs() {
  return request('/contractor-jobs')
}

export async function createContractorJob(job) {
  return request('/contractor-jobs', { method: 'POST', body: JSON.stringify(job) })
}

export async function updateContractorJob(id, updates) {
  return request(`/contractor-jobs/${id}`, { method: 'PATCH', body: JSON.stringify(updates) })
}

export async function deleteContractorJob(id) {
  return request(`/contractor-jobs/${id}`, { method: 'DELETE' })
}

export async function getContractorLinkInvites() {
  return request('/contractor-links')
}

export async function confirmContractorLink(jobId) {
  return request(`/contractor-links/${jobId}/confirm`, { method: 'POST' })
}

export async function declineContractorLink(jobId) {
  return request(`/contractor-links/${jobId}/decline`, { method: 'POST' })
}

export async function searchContractors({ lat, lng, trade }) {
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng) })
  if (trade) params.set('trade', trade)
  return request(`/contractor-directory/search?${params}`)
}

export async function getContractorPublicProfile(userId) {
  return request(`/contractor-directory/${userId}`)
}

export async function matchContractorProfile({ email, phone }) {
  const params = new URLSearchParams()
  if (email) params.set('email', email)
  if (phone) params.set('phone', phone)
  return request(`/contractor-directory/match?${params}`)
}

export async function createContractorReview(review) {
  return request('/contractor-reviews', { method: 'POST', body: JSON.stringify(review) })
}

export async function updateContractorReview(id, updates) {
  return request(`/contractor-reviews/${id}`, { method: 'PATCH', body: JSON.stringify(updates) })
}

export async function deleteContractorReview(id) {
  return request(`/contractor-reviews/${id}`, { method: 'DELETE' })
}

export async function replyToContractorReview(id, text) {
  return request(`/contractor-reviews/${id}/reply`, { method: 'PUT', body: JSON.stringify({ text }) })
}

export async function deleteContractorReviewReply(id) {
  return request(`/contractor-reviews/${id}/reply`, { method: 'DELETE' })
}

export async function getContractorAnalytics() {
  return request('/contractor-analytics')
}

export async function recordContractorContactClick(userId, channel) {
  return request(`/contractor-directory/${userId}/contact-click`, { method: 'POST', body: JSON.stringify({ channel }) })
}

export async function getContractorCampaigns() {
  return request('/contractor-campaigns')
}

export async function createContractorCampaign(campaign) {
  return request('/contractor-campaigns', { method: 'POST', body: JSON.stringify(campaign) })
}

export async function updateContractorCampaign(id, updates) {
  return request(`/contractor-campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(updates) })
}

export async function deleteContractorCampaign(id) {
  return request(`/contractor-campaigns/${id}`, { method: 'DELETE' })
}

export async function updatePropertyLocation(propertyId, { lat, lng }) {
  return request(`/ownership/properties/${propertyId}/location`, {
    method: 'PATCH', body: JSON.stringify({ lat, lng }),
  })
}
